'use client';

import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { IconLoader2 } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
    Field,
    FieldGroup,
    FieldLabel,
    FieldError,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { LoginFormData } from '@/hooks/use-login-form';

interface CredentialsStepProps {
    form: UseFormReturn<LoginFormData>;
    onSubmit: (data: LoginFormData) => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

export function CredentialsStep({
    form,
    onSubmit,
    isLoading,
    error,
}: CredentialsStepProps) {
    const t = useTranslations('auth');
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        // SECURITY: method="post" + noValidate + the empty action are
        // a defence-in-depth measure against credential leakage via
        // the URL bar. React Hook Form's handleSubmit calls
        // preventDefault() on the synthetic event, but if JS hasn't
        // hydrated yet (slow network, first paint), if a password
        // manager auto-submits before hydration, or if anything in
        // the handler chain throws before preventDefault() runs, the
        // browser falls back to native submission. With method="get"
        // (the HTML default) that fallback serialises every named
        // input — including the password — into the URL query string,
        // where it lands in browser history, server access logs,
        // reverse-proxy logs, and the HTTP Referer header. method="post"
        // keeps credentials in the request body even on the fallback
        // path; the page route will simply 405 the unintended POST,
        // which is a vastly better failure mode than a credential leak.
        <form
            method="post"
            action=""
            noValidate
            onSubmit={handleSubmit(onSubmit)}
        >
            <FieldGroup>
                {error && (
                    <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/50 rounded-md">
                        {error}
                    </div>
                )}
                <Field>
                    <FieldLabel htmlFor="email">{t('email')}</FieldLabel>
                    <Input
                        id="email"
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        disabled={isLoading}
                        {...register('email', { required: t('emailRequired') })}
                    />
                    {errors.email && (
                        <FieldError>{errors.email.message}</FieldError>
                    )}
                </Field>
                <Field>
                    <div className="flex items-center">
                        <FieldLabel htmlFor="password">{t('password')}</FieldLabel>
                        <a
                            href="#"
                            className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                        >
                            {t('forgotPassword')}
                        </a>
                    </div>
                    <PasswordInput
                        id="password"
                        disabled={isLoading}
                        {...register('password', { required: t('passwordRequired') })}
                    />
                    {errors.password && (
                        <FieldError>{errors.password.message}</FieldError>
                    )}
                </Field>
                <Field>
                    <Button type="submit" disabled={isLoading} className="w-full">
                        {isLoading ? (
                            <>
                                <IconLoader2 className="size-4 mr-2 animate-spin" />
                                {t('loggingIn')}
                            </>
                        ) : (
                            t('login')
                        )}
                    </Button>
                </Field>
            </FieldGroup>
        </form>
    );
}
